import { Controller } from '@hotwired/stimulus'
import { stringToClassnames } from '../utils'

interface TurboFrameElement extends HTMLElement {
  reload: () => void
  src?: string
}
interface TabGroupBehaviorOptions {
  focus?: boolean
  updateUrl?: boolean
  tab?: string
}
interface TabSelectEvent extends Event {
  params: TabGroupBehaviorOptions
}

export default class TabsController extends Controller<HTMLElement> {
  declare initialValue: string
  declare nameValue: string
  declare readonly hasInitialValue: boolean
  declare readonly tabListTargets: HTMLElement[]
  declare readonly tabPanelTargets: HTMLElement[]
  declare readonly tabPanelFrameLinkTargets: HTMLAnchorElement[]
  declare readonly tabTargets: HTMLElement[]

  static targets = ['tab', 'tabList', 'tabPanel', 'tabPanelFrameLink']
  static values = {
    initial: String, // The ID of the initial tab to show
    name: String,
  }

  get initialTab() {
    return this.hasInitialValue ? this.tabs.find(tab => tab.id === this.initialValue) : this.tabs.at(0) // Default to the first tab
  }

  get panels() {
    return this.tabPanelTargets
  }

  get selectedIndex() {
    return this.tabs.indexOf(this.selectedTab)
  }

  get selectedTab() {
    return this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true')
  }

  get tabs() {
    return this.tabTargets
  }

  get urlTab() {
    const groupName = this.nameValue
    const param = new URLSearchParams(window.location.search).get(groupName)

    return this.tabs.find(tab => tab.id === param)
  }

  connect() {
    const tabToShowOnFirstRender =
      this.urlTab || // Look for a tab in the URL
      this.initialTab // Look for a tab that's manually selected in the HTML

    // Prevent focus and url update on first render
    this.select(tabToShowOnFirstRender, {
      focus: false,
      updateUrl: false,
    })
    this.dispatch('connect')
  }

  tabPanelFrameLinkTargetConnected(element: HTMLAnchorElement) {
    element.addEventListener('click', this.#handlePanelFrameLinkClick.bind(this))
  }

  tabPanelFrameLinkTargetDisconnected(element: HTMLAnchorElement) {
    element.removeEventListener('click', this.#handlePanelFrameLinkClick.bind(this))
  }

  handleKeyboardNav(event: KeyboardEvent & TabSelectEvent) {
    const prevKeys = ['ArrowLeft', 'ArrowUp']
    const nextKeys = ['ArrowRight', 'ArrowDown']

    if (![...prevKeys, ...nextKeys].includes(event.key)) return
    if (prevKeys.includes(event.key)) this.#selectPrevTab(event.params || {})
    if (nextKeys.includes(event.key)) this.#selectNextTab(event.params || {})
  }

  /**
   * @param elemOrEvent
   * This method can be triggered programmatically -OR- by a click event.
   * If triggered programmatically, `elemOrEvent` will be an HTMLElement object.
   * If triggered by a click event, `elemOrEvent` will be an Event object.
   *
   * @param options `Object { focus: boolean, updateUrl: boolean}`
   * - `focus`: Default: `true`. Determines whether or not to focus on the tab when selecting.
   * - `updateUrl`: Default `true`. Determines whether or not to update the URL when switching tabs
   */
  select(elemOrEvent: HTMLElement | TabSelectEvent, { focus = true, updateUrl = true } = {}) {
    const oldTab = this.selectedTab
    const isEvent = elemOrEvent instanceof Event
    const newTab = (
      isEvent
        ? 'tab' in elemOrEvent.params
          ? this.element.querySelector(`#${elemOrEvent.params.tab}`)
          : elemOrEvent.currentTarget
        : elemOrEvent
    ) as HTMLElement

    this.#updateTabs(oldTab, newTab)
    this.#updateTabPanels(oldTab, newTab)

    const behavior: TabGroupBehaviorOptions = {
      focus: isEvent ? (elemOrEvent.params?.focus ?? focus) : focus,
      updateUrl: isEvent ? (elemOrEvent.params?.updateUrl ?? updateUrl) : updateUrl,
    }

    if (behavior.focus) newTab.focus()
    if (behavior.updateUrl) this.#updateUrl(newTab.id)

    if (oldTab !== newTab) {
      this.dispatch('change', { detail: { oldTab, newTab } })
    }
  }

  // Private methods

  /**
   * Get the classes to add and remove from the tab or panel during selection
   */
  #getDataClasses(elem: HTMLElement) {
    const { tabDeselectedClasses, tabSelectedClasses } = elem.dataset
    const selectedClasses = stringToClassnames(tabSelectedClasses)
    const deselectedClasses = stringToClassnames(tabDeselectedClasses)

    return [deselectedClasses, selectedClasses]
  }

  /**
   * Allows the user to navigate from one tab panel's turbo-frame to another
   * while also updating the `src` of the new tab panel's turbo-frame.
   */
  #handlePanelFrameLinkClick(event: MouseEvent) {
    const link = event.target as HTMLAnchorElement
    const frame = document.getElementById(link.dataset.turboFrame) as TurboFrameElement
    const panel = frame.closest('[role="tabpanel"]')
    const tab = document.getElementById(panel.getAttribute('aria-labelledby'))

    frame.src = link.href
    this.select(tab)
  }

  #selectNextTab(params: TabGroupBehaviorOptions = {}) {
    const nextIndex = this.selectedIndex + 1
    const nextTab = this.tabTargets[nextIndex] || this.tabTargets.at(0)

    this.select(nextTab, params)
  }

  #selectPrevTab(params: TabGroupBehaviorOptions = {}) {
    const prevIndex = this.selectedIndex - 1
    const prevTab = this.tabTargets[prevIndex] || this.tabTargets.at(-1)

    this.select(prevTab, params)
  }

  #updateTabs(oldTab: HTMLElement, newTab: HTMLElement) {
    if (oldTab === newTab) return

    this.tabTargets.forEach(elem => {
      const [deselectedClasses, selectedClasses] = this.#getDataClasses(elem)

      // If this tab is the previously-selected tab, deselect it.
      // But also deselect it if there was no previously-selected tab.
      // (This happens during first render.)
      if ((!oldTab && elem !== newTab) || elem === oldTab) {
        elem.classList.remove(...selectedClasses)
        elem.classList.add(...deselectedClasses)
        elem.setAttribute('aria-selected', 'false')
        elem.setAttribute('tabindex', '-1')
      }

      // If this tab is the target tab, select it.
      if (elem === newTab) {
        elem.classList.remove(...deselectedClasses)
        elem.classList.add(...selectedClasses)
        elem.setAttribute('aria-selected', 'true')
        elem.setAttribute('tabindex', '0')
      }
    })
  }

  #updateTabPanels(oldTab: HTMLElement, newTab: HTMLElement) {
    if (oldTab === newTab) return

    this.panels.forEach(elem => {
      const tabId = elem.getAttribute('aria-labelledby')
      const turboFrame = elem.querySelector<TurboFrameElement>('& > turbo-frame')
      const [deselectedClasses, selectedClasses] = this.#getDataClasses(elem)

      // If this panel belongs to the previously-selected tab, deselect it.
      // But also deselect it if there was no previously-selected tab.
      // (This happens during first render.)
      if ((!oldTab && tabId !== newTab.id) || tabId === oldTab?.id) {
        elem.classList.remove(...selectedClasses)
        elem.classList.add(...deselectedClasses)
        elem.setAttribute('tabindex', '-1')
        if ('disabled' in elem) elem.disabled = true
      }

      // If this panel belongs to the target tab, select it.
      else if (tabId === newTab.id) {
        elem.classList.remove(...deselectedClasses)
        elem.classList.add(...selectedClasses)
        elem.setAttribute('tabindex', '0')
        if ('disabled' in elem) elem.disabled = false

        if (!turboFrame) return

        if (turboFrame.hasAttribute('disabled')) {
          turboFrame.removeAttribute('disabled')
        }

        if (turboFrame.hasAttribute('complete')) {
          turboFrame.reload()
        }
      }
    })
  }

  #updateUrl(tabId: string) {
    const url = new URL(window.location.toString())
    url.searchParams.set(this.nameValue, tabId)
    history.replaceState({}, '', url)
  }
}
